(ns shared.core.components.recharts.bar-chart
  (:require [clojure.string :as s]
            [shared.core.style :as style]
            [shared.core.util :as util]
            [shared.core.reactive :as re]
            [shared.core.components.recharts.util :as recharts.util]
            [shared.core.components.recharts.tooltip :as tooltip]))

(style/add-css [".recharts-bar-rectangle:hover"
                {:filter "none !important"}]
               [".recharts-active-bar"
                {:filter "none !important"}]
               ;; Only a chart whose bars actually do something on click invites one.
               [".bar-chart-clickable .recharts-bar-rectangle"
                {:cursor "pointer"}]
               ;; The line series' hover dot, in CSS so running along the line doesn't
               ;; re-render the chart.
               [".bar-chart-line-point-active"
                {:display "none"}]
               [".bar-chart-line-point:hover .bar-chart-line-point-active"
                {:display "block"}])

(defn get-keyword-or-string [k m]
  (or ((keyword k) m) (get m (name k))))

(defn build-tooltip-row
  [{:keys [data-keys-config total hovered-key label-transf-fn value-transf-fn show-percent?]
    :or {show-percent? true}
    :as context}
   {:keys [data-key display-name value line?]}]
  (when (and value (pos? value))
    (let [dk-r (get data-keys-config data-key)
          fill (get-keyword-or-string :fill dk-r)
          highlight-color (get-keyword-or-string :highlight-color dk-r)
          opacity (get-keyword-or-string :opacity dk-r)
          indicator-color (if (some-> fill (s/starts-with? "rgba"))
                            fill
                            (util/hex->rgba fill (or opacity 1)))
          is-hovered (= data-key hovered-key)
          pct (if (pos? total) (* 100 (/ value total)) 0)
          rounded-pct (some-> pct (js/Math.floor))]
      {:key (str data-key)
       :label (if label-transf-fn
                (label-transf-fn display-name)
                (util/->capitalize-words display-name))
       :value (cond
                value-transf-fn (value-transf-fn value context)
                (number? value) (util/readable-number value)
                :else value)
       ;; A line series is on its own axis, so it has no share of the bar total.
       :percent (when (and show-percent? (not line?))
                  (str (cond
                         (< rounded-pct 1) "<1"
                         :else (.toFixed pct 1))
                       "%"))
       :color fill
       :highlight-color (or highlight-color (util/hex->rgba fill 0.12))
       :highlight? is-hovered
       :indicator {:color indicator-color
                   :shape (if line? :line :square)}})))

(defn linear-regression-points
  "Given a seq of y-values (x taken as the index), returns a vector of least-squares fitted y-values
   aligned to the input (one per input element). Non-numeric values are ignored when fitting but still
   get a fitted point. Returns nil when there aren't at least two numeric points to fit a line."
  [ys]
  (let [pts (->> ys
                 (mapv (fn [y] (if (nil? y) 0 y)))
                 (map-indexed vector)
                 (filter (fn [[_ y]] (number? y))))
        n (count pts)]
    (when (>= n 2)
      (let [xs (map first pts)
            yvs (map second pts)
            sx (reduce + xs)
            sy (reduce + yvs)
            sxx (reduce + (map #(* % %) xs))
            sxy (reduce + (map (fn [[x y]] (* x y)) pts))
            denom (- (* n sxx) (* sx sx))]
        (when-not (zero? denom)
          (let [slope (/ (- (* n sxy) (* sx sy)) denom)
                intercept (/ (- sy (* slope sx)) n)]
            (mapv (fn [i] (max (+ intercept (* slope i)) 0)) (range (count ys)))))))))

(defn ->percent-data-key [dk]
  (when (not-empty dk)
    (str (name dk) "-percent")))

(defn with-trend-lines
  "Assocs a `<trend-key>-trend` field onto each row of `chart-data` for every key in `trend-keys`,
   computed as the least-squares regression of that key's values across the rows."
  [chart-data trend-keys {:keys [value-fn convert-values-to-percents?]}]
  (reduce (fn [cd tk]
            (let [tk (if convert-values-to-percents? (->percent-data-key tk) tk)]
              (if-let [trend (linear-regression-points (map (if value-fn #(value-fn % tk) #(get % tk)) cd))]
                (mapv (fn [row t] (assoc row (str tk "-trend") t)) cd trend)
                cd)))
          chart-data
          trend-keys))

(defn bar-node?
  [node]
  (when (.-classList node)
    (or (.contains (.-classList node) "recharts-bar-rectangle")
        (.contains (.-classList node) "recharts-rectangle"))))

(defn click-target-bar?
  [target]
  (loop [node target
         depth 0]
    (cond
      (nil? node) false
      (>= depth 20) false
      (bar-node? node) true
      :else (recur (.-parentNode node) (inc depth)))))

(defn custom-stack-tooltip [{:keys [tooltip-state* mouse-position* data-keys-config header-transf-fn label-transf-fn
                                    value-transf-fn show-percent? column-template total-override percent-style
                                    show-double-click-message? header-transf-name-fn style-map x-key]
                             :or {show-percent? true}
                             :as r}]
  (when-let [{:keys [data values hovered-key line-hover?]} @tooltip-state*]
    (let [{:keys [x y]} @mouse-position*
          total (or total-override (->> values (remove :line?) (mapv :value) (reduce +)))
          context {:data-keys-config data-keys-config
                   :total total
                   :hovered-key hovered-key
                   :label-transf-fn label-transf-fn
                   :value-transf-fn value-transf-fn
                   :state @tooltip-state*
                   :show-percent? show-percent?}
          rows (->> values
                    (map #(build-tooltip-row context %))
                    (remove nil?)
                    vec)
          header-label-data-val (or (get data x-key)
                                    (get data (keyword x-key))
                                    (get data (util/->kebab-case-keyword x-key)))]
      [tooltip/tooltip-panel {:style-map (merge (tooltip/floating-style x y {:tooltip-width 320})
                                                style-map)
                              :numeric-align :right
                              :column-template (or column-template "minmax(140px,1fr) auto")
                              :header {:label (cond
                                                header-transf-fn (header-transf-fn r)
                                                header-transf-name-fn (header-transf-name-fn header-label-data-val)
                                                :else (util/->capitalize-words header-label-data-val))
                                       :value nil
                                       :percent nil}
                              :rows rows
                              :percent-style percent-style
                              ;; Only the bars carry a double-click handler.
                              :show-double-click-message? (and show-double-click-message?
                                                               (not line-hover?))}])))

(defn- line-dot
  "One point of the optional line series: a transparent hit area (the visible dot is
   too small to hover), the dot itself, and the hover dot — a bigger dot with a white
   ring, the same treatment recharts' own `activeDot` gives the line chart.
   That can't be reused here (nothing drives recharts' tooltip state), so hovering
   calls `on-hover` with the point's data row for the tooltip and CSS swaps the dot.
   Fields are read off `props` rather than js->clj'd: recharts hangs the whole point
   list on every dot."
  [props {:keys [dot? fill on-hover on-leave]}]
  (let [cx (.-cx props)
        cy (.-cy props)
        point? (and cx cy)]
    (re/as-element
      [:g (cond-> {:key (.-key props)
                   :class "bar-chart-line-point"}
                  point?
                  (assoc :on-mouse-enter #(on-hover (js->clj (.-payload props) :keywordize-keys true))
                         :on-mouse-leave on-leave))
       (when point?
         [:circle {:cx cx
                   :cy cy
                   :r 8
                   :fill "transparent"}])
       (when (and point? dot?)
         [:circle {:cx cx
                   :cy cy
                   :r 3
                   :fill fill}])
       (when point?
         [:circle {:class "bar-chart-line-point-active"
                   :cx cx
                   :cy cy
                   :r 4
                   :fill fill
                   ;; White in both themes, like the line chart's recharts activeDot.
                   :stroke "#fff"
                   :stroke-width 2}])])))

(defn bar-chart
  [_]
  (let [click-timer* (atom nil)
        tooltip-state* (re/ratom nil)
        mouse-position* (re/ratom {:x 0 :y 0})
        ;; When set, only this segment (series key) is fully opaque; others fade.
        selected-key* (re/ratom nil)
        ;; Track specific clicked bar (x/name) for outlining that one rectangle
        selected-datum* (re/ratom nil)]
    (fn [{:keys [data data-keys-config bar-size chart-height chart-width legend? tooltip? tooltip-config x-axis?
                 x-axis-config y-axis? y-axis-config y-max grid? grid-config chart-container-style chart-style
                 data-keys-sort-fn total-override bar-config legend-config margin data-keys x-key stack-id
                 trend-keys trend-config convert-values-to-percents? reference-areas dynamic-margins?
                 alternate-x-set alternate-lighten alternate-darken horizontal? goal goal-config line-config]
          :or {bar-size 20
               legend? true
               tooltip? true
               x-axis? true
               y-axis? true
               grid? true
               x-key "name"
               dynamic-margins? true
               alternate-lighten 0.15
               alternate-darken 0.15}}]
      (let [value-key (or (some-> data-keys (first)) :value)
            chart-data (if data-keys
                         data
                         (->> data
                              (mapv (fn [i]
                                      (merge {:name (:name i)}
                                             (->> (:values i)
                                                  (mapv (fn [v] [(:key v) (:value v)]))
                                                  (into {})))))))
            data-keys (or data-keys (keys data-keys-config))
            chart-data (if (and convert-values-to-percents? (not-empty data-keys))
                         (mapv (fn [rec]
                                 (let [total (->> data-keys
                                                  (mapv (fn [dk] (get rec dk 0)))
                                                  (apply +))]
                                   (merge rec
                                          (->> data-keys
                                               (mapv (fn [dk]
                                                       (let [dk-val (get rec dk 0)]
                                                         [(->percent-data-key dk)
                                                          (or (when (pos? total)
                                                                (* 100 (/ dk-val total)))
                                                              0)])))
                                               (into {})))))
                               chart-data)
                         chart-data)
            chart-data (if (not-empty trend-keys)
                         (with-trend-lines chart-data
                                           trend-keys
                                           (merge {:convert-values-to-percents? convert-values-to-percents?}
                                                  trend-config))
                         chart-data)
            data-keys-config (or data-keys-config
                                 (when data-keys
                                   (->> data-keys
                                        (mapv (fn [dk]
                                                [dk {:fill (recharts.util/color-from-string dk)}]))
                                        (into {}))))
            bar-count (if data-keys
                        (count data)
                        (->> data
                             (map #(count (:values %)))
                             (apply +)))
            chart-height (or chart-height
                             (if horizontal?
                               (let [per-row (if (and (not stack-id) (> (count data-keys) 1))
                                               (* bar-size (count data-keys))
                                               bar-size)]
                                 (+ 80 (* bar-count (+ per-row 16))))
                               350))
            chart-width (or chart-width
                            (when-not horizontal?
                              (+ 100 (* bar-count 60))))
            fill-width? (and horizontal? (nil? chart-width))
            max-value (if data-keys
                        (some->> data
                                 (map #(get % value-key))
                                 (apply max))
                        (some->> data
                                 (mapcat :values)
                                 (map :value)
                                 (apply max)))
            y-max (or y-max (when max-value (Math/ceil (* max-value 1.05))))
            sorted-data-keys (if data-keys-sort-fn
                               (data-keys-sort-fn data-keys)
                               data-keys)
            ;; Stacked charts order tooltip rows top->bottom by the incoming data-keys;
            ;; otherwise fall back to the data-keys-config order.
            tooltip-keys (if stack-id
                           data-keys
                           (keys data-keys-config))
            ;; What a bar click does: whatever the caller wired up, or the built-in
            ;; series highlight — which only has an effect with several series to dim.
            bars-clickable? (boolean (or (:on-click bar-config)
                                         (:on-double-click bar-config)
                                         (> (count data-keys) 1)))
            ;; An optional second series drawn as a line, on its own right-hand value axis
            ;; unless its scale is close enough to share the bars' (see below). Its column
            ;; rides along on the same rows as the bars.
            line-key (when-not horizontal? (:data-key line-config))
            line-label (or (not-empty (:label line-config)) line-key)
            ;; Same derivation the bars and the line chart use, so one series reads the
            ;; same color wherever it is drawn.
            line-stroke (when line-key
                          (or (:stroke line-config)
                              (recharts.util/color-from-string line-key)))
            line-axis-config (:axis-config line-config)
            line-max (when line-key
                       (some->> chart-data
                                (map #(get-keyword-or-string line-key %))
                                (filter number?)
                                (not-empty)
                                (apply max)))
            ;; Two value axes within 25% of each other read as duplicates, so the line
            ;; rides the bars' axis instead of drawing a near-identical second one.
            ;; Authoring an `:axis-config` opts out — that asks for its own axis.
            line-shares-axis? (let [bar-axis-max (if convert-values-to-percents? 100 y-max)]
                                (boolean (and line-key
                                              (nil? line-axis-config)
                                              (pos? (or line-max 0))
                                              (pos? (or bar-axis-max 0))
                                              ;; the percent axis is locked to 0-100, so a
                                              ;; line above it would clip, not share
                                              (or (not convert-values-to-percents?)
                                                  (<= line-max 100))
                                              (<= (/ (max bar-axis-max line-max)
                                                     (min bar-axis-max line-max))
                                                  1.25))))
            line-axis-id (if line-shares-axis? 0 "line")
            line-axis-width (when (and line-key
                                       (not line-shares-axis?)
                                       dynamic-margins?
                                       (not (:width line-axis-config)))
                              (+ (recharts.util/fit-y-axis-width
                                   [(util/readable-number (or line-max 0))])
                                 (if (not-empty (:label line-config)) 16 0)))
            ;; The tooltip rows for one data row — every bar series plus the line, so
            ;; hovering either shows the same values.
            tooltip-values (fn [row]
                             (cond-> (mapv (fn [k]
                                             {:data-key k
                                              :display-name k
                                              :value (get row (keyword k))})
                                           tooltip-keys)
                                     line-key
                                     (conj {:data-key line-key
                                            :display-name line-label
                                            :value (get row (keyword line-key))
                                            :line? true})))
            y-axis-width (when (and dynamic-margins? y-axis? (not (:width y-axis-config)))
                           (recharts.util/fit-y-axis-width
                             (cond
                               horizontal? (map #(get-keyword-or-string x-key %) chart-data)
                               convert-values-to-percents? ["100%"]
                               ;; A shared axis stretches to whichever series runs higher.
                               :else [(util/readable-number (cond-> (or y-max max-value 0)
                                                                    line-shares-axis?
                                                                    (max line-max)))])))
            x-axis-height (when (and dynamic-margins? x-axis? (not horizontal?) (not (:height x-axis-config)))
                            (recharts.util/fit-x-axis-height
                              (map #(get-keyword-or-string x-key %) chart-data)
                              (:angle x-axis-config)))
            ;; Lock the percent domain: stacked percent rows can float-sum to
            ;; 100.00000000000001, which otherwise expands the domain past 100 and
            ;; recharts regenerates uneven ticks (0/30/60/100) instead of 0/25/50/75/100.
            value-axis-props (cond-> {:domain [0 (if convert-values-to-percents? 100 y-max)]}
                                     convert-values-to-percents? (assoc :allowDataOverflow true))
            value-tick-transf-fn (if convert-values-to-percents?
                                   (fn [p] (str (js/Math.round (:value p)) "%"))
                                   (fn [p] (util/readable-number (:value p))))
            goal-label (when goal
                         (or (get-keyword-or-string :label goal-config)
                             (str "Goal: " (if convert-values-to-percents?
                                             (str (js/Math.round goal) "%")
                                             (util/readable-number goal)))))
            goal-line (when goal
                        (let [stroke (or (get-keyword-or-string :color goal-config)
                                         (style/alc-color "Text/primary"))]
                          [recharts.util/re-reference-line
                           (merge (if horizontal? {:x goal} {:y goal})
                                  {:stroke stroke
                                   :strokeWidth 1.5
                                   :strokeDasharray "6 4"
                                   :ifOverflow "extendDomain"
                                   :label {:value goal-label
                                           :position (if horizontal? "top" "right")
                                           :fill stroke
                                           :fontSize 11
                                           :fontFamily style/roboto-font-family}})]))
            chart-margin (cond-> (or margin {:top 20 :right 30 :left 20 :bottom 20})
                                 (and goal (not horizontal?))
                                 (update :right #(max (or % 0)
                                                      (+ 16 (js/Math.ceil
                                                              (recharts.util/measure-text-width
                                                                goal-label
                                                                recharts.util/tick-font))))))
            ;; Angled x labels run left of their tick and clip without room for it.
            chart-margin (cond-> chart-margin
                                 (and x-axis? (not horizontal?) (:angle x-axis-config))
                                 (update :left #(max (or % 0)
                                                     (- (recharts.util/fit-x-label-left-overflow
                                                          (map (fn [d] (get-keyword-or-string x-key d)) chart-data)
                                                          (:angle x-axis-config))
                                                        (or y-axis-width 0)))))
            chart [(if (or line-key (not-empty trend-keys))
                     recharts.util/re-composed-chart
                     recharts.util/re-bar-chart)
                   (merge {:data (clj->js chart-data)
                           :responsive true
                           :height chart-height
                           :margin chart-margin}
                          (when chart-width {:width chart-width})
                          (when horizontal? {:layout "vertical"}))
                   (when grid?
                     [recharts.util/re-cartesian-grid (merge {:stroke-dasharray "3 3"
                                                              :stroke (style/alc-color "lines/chart_grid")
                                                              :vertical (boolean horizontal?)
                                                              :horizontal (not horizontal?)}
                                                             grid-config)])
                   (when x-axis?
                     (if horizontal?
                       [recharts.util/re-x-axis (merge {:type "number"
                                                        :tick #(recharts.util/custom-rotated-x-tick
                                                                 %
                                                                 (merge {:value-transf-fn value-tick-transf-fn}
                                                                        x-axis-config))
                                                        :tickLine false
                                                        :axisLine false}
                                                       value-axis-props
                                                       x-axis-config)]
                       [recharts.util/re-x-axis (merge {:dataKey x-key
                                                        :tick #(recharts.util/custom-rotated-x-tick
                                                                 %
                                                                 x-axis-config)
                                                        :height (or x-axis-height 80)
                                                        :interval 0}
                                                       x-axis-config)]))
                   ;; A line series needs the bars' axis to exist even when it's hidden, so the
                   ;; two axes can be told apart by id.
                   (when (or y-axis? line-key)
                     (if horizontal?
                       [recharts.util/re-y-axis (merge {:type "category"
                                                        :dataKey x-key
                                                        :tick #(recharts.util/custom-y-tick
                                                                 %
                                                                 (merge {:value-transf-fn :value}
                                                                        y-axis-config))
                                                        :tickLine false
                                                        :axisLine false
                                                        :interval 0}
                                                       (when y-axis-width {:width y-axis-width})
                                                       y-axis-config)]
                       [recharts.util/re-y-axis (merge {:tick #(recharts.util/custom-y-tick
                                                                 %
                                                                 (merge (when convert-values-to-percents?
                                                                          {:value-transf-fn (fn [v] (str (js/Math.round (:value v))
                                                                                                         "%"))})
                                                                        y-axis-config))
                                                        :tickLine false
                                                        :axisLine false}
                                                       value-axis-props
                                                       (when y-axis-width {:width y-axis-width})
                                                       y-axis-config
                                                       (when-not y-axis? {:hide true}))]))
                   (when (and line-key (not line-shares-axis?))
                     [recharts.util/re-y-axis (merge {:yAxisId line-axis-id
                                                      :orientation "right"
                                                      :tick #(recharts.util/custom-y-tick
                                                               %
                                                               (merge {:orientation :right}
                                                                      line-axis-config))
                                                      :tickLine false
                                                      :axisLine false}
                                                     (when line-axis-width {:width line-axis-width})
                                                     (when (not-empty (:label line-config))
                                                       {:label {:value (:label line-config)
                                                                :angle 90
                                                                :position "insideRight"
                                                                :fill (style/alc-color "Text/primary")
                                                                :fontSize 11
                                                                :fontFamily style/roboto-font-family}})
                                                     line-axis-config)])
                   (doall
                     (map-indexed
                       (fn [idx {:keys [x1 x2 fill opacity]}]
                         [recharts.util/re-reference-area
                          {:key (str "ref-area-" idx)
                           :x1 x1
                           :x2 x2
                           :ifOverflow "extendDomain"
                           :fill (or fill (style/alc-color "lines/chart_grid"))
                           :fillOpacity (or opacity 0.35)
                           :stroke "none"}])
                       reference-areas))
                   (doall
                     (map
                       (fn [data-key]
                         (let [{:keys [on-click on-double-click]} bar-config
                               on-click (or on-click
                                            (or on-click
                                                (fn [clj-data]
                                                  (let [x-name (get-in clj-data [:payload :name])]
                                                    (if (and (= @selected-key* data-key)
                                                             (= @selected-datum* x-name))
                                                      (do
                                                        (reset! selected-key* nil)
                                                        (reset! selected-datum* nil))
                                                      (do
                                                        (reset! selected-key* data-key)
                                                        (reset! selected-datum* x-name)))))))]
                           (into
                             [recharts.util/re-bar (merge {:key data-key
                                                           :dataKey (if convert-values-to-percents?
                                                                      (->percent-data-key data-key)
                                                                      data-key)
                                                           :stackId stack-id
                                                           :fill (recharts.util/color-from-string data-key)
                                                           :stroke (style/alc-color "Background/card_hex")
                                                           :strokeWidth 1
                                                           :barSize bar-size
                                                           :name data-key
                                                           :onMouseEnter (fn [data _e]
                                                                           (let [clj-data (js->clj data :keywordize-keys true)]
                                                                             (reset! tooltip-state* {:hovered-key data-key
                                                                                                     :values (tooltip-values (:payload clj-data))
                                                                                                     :data clj-data})))
                                                           :onMouseLeave (fn [_ _]
                                                                           (reset! tooltip-state* nil))
                                                           :onClick (if (and on-click on-double-click)
                                                                      (fn [d index e]
                                                                        (let [clj-data (-> (js->clj d :keywordize-keys true)
                                                                                           (assoc :tooltip-state* tooltip-state*))]
                                                                          (when @click-timer* (js/clearTimeout @click-timer*))
                                                                          (reset! click-timer* (js/setTimeout (fn [] (on-click
                                                                                                                       clj-data
                                                                                                                       index
                                                                                                                       e))
                                                                                                              250))))
                                                                      on-click)
                                                           :onDoubleClick (when on-double-click
                                                                            (fn [d index e]
                                                                              (when @click-timer*
                                                                                (js/clearTimeout @click-timer*)
                                                                                (reset! click-timer* nil))
                                                                              (on-double-click (-> (js->clj d :keywordize-keys true)
                                                                                                   (assoc :tooltip-state* tooltip-state*))
                                                                                               index
                                                                                               e)
                                                                              (util/stop-event e)))}
                                                          (get data-keys-config data-key))]
                             (map-indexed
                               (fn [idx point]
                                 (let [point-name (:name point)
                                       clicked-series (= @selected-key* data-key)
                                       clicked? (and clicked-series (= @selected-datum* point-name))
                                       fill-opacity (cond
                                                      clicked? 1
                                                      clicked-series 1
                                                      (some? @selected-key*) 0.1
                                                      :else 1)
                                       alternating? (not-empty alternate-x-set)
                                       alt? (contains? alternate-x-set (get point x-key))
                                       base-fill (or (get-keyword-or-string :fill (get data-keys-config data-key))
                                                     (recharts.util/color-from-string data-key))
                                       cell-fill (when (and alternating? base-fill (s/starts-with? base-fill "#"))
                                                   (if alt?
                                                     (util/lighten-hex base-fill alternate-lighten)
                                                     (util/darken-hex base-fill alternate-darken)))]
                                   [recharts.util/re-cell (cond-> {:key (str (name data-key) "-" idx)
                                                                   :fillOpacity fill-opacity}
                                                                  cell-fill (assoc :fill cell-fill))]))
                               chart-data))))
                       sorted-data-keys))
                   (when (not-empty trend-keys)
                     (doall
                       (map
                         (fn [trend-key]
                           (let [{:keys [fill]} (get data-keys-config trend-key)]
                             [recharts.util/re-line {:key (str trend-key "-trend")
                                                     :type "linear"
                                                     :dataKey (str trend-key "-trend")
                                                     :stroke (or (:stroke trend-config)
                                                                 fill
                                                                 (recharts.util/color-from-string trend-key))
                                                     :strokeWidth (or (:stroke-width trend-config) 2)
                                                     :strokeDasharray (or (:stroke-dasharray trend-config) "6 4")
                                                     :dot false
                                                     :activeDot false
                                                     :isAnimationActive false
                                                     :legendType "none"
                                                     :name (str trend-key "-trend")}]))
                         (if convert-values-to-percents?
                           (mapv ->percent-data-key trend-keys)
                           trend-keys))))
                   (when line-key
                     [recharts.util/re-line (merge {:yAxisId line-axis-id
                                                    :type (if (:smooth? line-config) "monotone" "linear")
                                                    :dataKey line-key
                                                    :name line-label
                                                    :stroke line-stroke
                                                    :strokeWidth (or (:stroke-width line-config) 2)
                                                    :dot #(line-dot % {:dot? (not (false? (:dot? line-config)))
                                                                       :fill line-stroke
                                                                       :on-hover (fn [row]
                                                                                   (reset! tooltip-state*
                                                                                           {:hovered-key line-key
                                                                                            :line-hover? true
                                                                                            :values (tooltip-values row)
                                                                                            :data row}))
                                                                       :on-leave (fn [_] (reset! tooltip-state* nil))})
                                                    :activeDot false
                                                    :connectNulls true
                                                    :isAnimationActive false
                                                    :legendType "none"}
                                                   (when (:stroke-dasharray line-config)
                                                     {:strokeDasharray (:stroke-dasharray line-config)}))])
                   goal-line]]
        [:div {:style (merge {:width "100%"
                              :display :flex
                              :flex-direction :column
                              :align-items :center
                              :position :relative
                              :gap 12}
                             chart-container-style)}
         [:div {:class (when bars-clickable? "bar-chart-clickable")
                :style (merge {:width (if fill-width? "100%" :auto)
                               :max-width "100%"
                               :height (if fill-width?
                                         chart-height
                                         (+ chart-height recharts.util/x-scrollbar-allowance))
                               :overflow-x :auto
                               :overflow-y :hidden}
                              chart-style)
                :on-mouse-move (fn [e]
                                 (reset! mouse-position* {:x (.-clientX e)
                                                          :y (.-clientY e)}))
                :on-click (fn [e]
                            (when-not (click-target-bar? (.-target e))
                              (reset! selected-key* nil)
                              (reset! selected-datum* nil)))}
          (if fill-width?
            [recharts.util/re-responsive-container {:width "100%"
                                                    :height chart-height}
             chart]
            chart)]
         (when tooltip?
           [custom-stack-tooltip (merge {:tooltip-state* tooltip-state*
                                         :mouse-position* mouse-position*
                                         ;; The line's own color, for its tooltip row only — the bars,
                                         ;; the legend and the series loop read the unmerged config.
                                         :data-keys-config (cond-> data-keys-config
                                                                   line-key
                                                                   (assoc line-key
                                                                          {:fill line-stroke
                                                                           ;; from the solid hex: the row tint reads
                                                                           ;; as a wash, not a block of the color
                                                                           :highlight-color (util/hex->rgba
                                                                                              (or (:stroke line-config)
                                                                                                  (util/str->hex line-key))
                                                                                              0.12)}))
                                         :total-override total-override
                                         :show-percent? (some-> data-keys (count) (> 1))
                                         :x-key x-key}
                                        tooltip-config)])
         (when legend?
           [recharts.util/standalone-legend
            (assoc legend-config
                   :entries (cond-> (mapv (fn [data-key]
                                            {:label data-key
                                             :color (get-keyword-or-string :fill (get data-keys-config data-key))})
                                          sorted-data-keys)
                                    line-key
                                    (conj {:label line-label
                                           :color line-stroke
                                           :shape :line})))])]))))